import {
  ApplicationInsights,
  ITelemetryItem,
} from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

const connectionString = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING;
const isProd = process.env.NODE_ENV === 'production';
const environment =
  process.env.REACT_APP_ENVIRONMENT || (isProd ? 'production' : 'development');

// Polyfill for crypto.randomUUID to work in test environment
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for test environment
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Check if we have stored signup session IDs (preserved across Auth0 redirect and across tabs)
// This allows us to correlate all signup journey events with the same sessionId/correlationId
const signupSessionId = localStorage.getItem('signup_session_id');
const signupCorrelationId = localStorage.getItem('signup_correlation_id');

let sessionId = signupSessionId || sessionStorage.getItem('app_session_id');
if (!sessionId) {
  sessionId = generateUUID();
  sessionStorage.setItem('app_session_id', sessionId);
}

let correlationId =
  signupCorrelationId || sessionStorage.getItem('app_correlation_id');
if (!correlationId) {
  correlationId = generateUUID();
  sessionStorage.setItem('app_correlation_id', correlationId);
}

const reactPlugin = new ReactPlugin();

const appInsights = new ApplicationInsights({
  config: {
    connectionString,
    enableAutoRouteTracking: true,
    extensions: [], // reactPlugin
    extensionConfig: {
      [reactPlugin.identifier]: { history: undefined },
    },
    disableAjaxTracking: true, // Temp true until we add CORS setup to BE
    disableFetchTracking: true, // Temp true until we add CORS setup to BE
    enableCorsCorrelation: true,
    distributedTracingMode: 1, // AI
    loggingLevelConsole: isProd ? 1 : 2, // 1=Error, 2=Warn, 3=Info, 4=Verbose
    enableRequestHeaderTracking: true,
    enableResponseHeaderTracking: true,
    sessionRenewalMs: 30 * 60 * 1000, // 30 min
    sessionExpirationMs: 24 * 60 * 60 * 1000, // 24 hours
    // Only add correlation headers to our own API domains (whitelist approach)
    // This prevents CORS issues with third-party services like HubSpot
    // that don't allow 'Request-Id' header in their CORS configuration
    /*
    correlationHeaderDomains: [
      'api.cirra.ai',
      'api-dev.cirra.ai',
      'api-staging.cirra.ai',
      'localhost',
    ],
    */
  },
});

// Initialize Application Insights with logging
try {
  if (isProd || process.env.REACT_APP_ENABLE_APPINSIGHTS === 'true') {
    if (!connectionString) {
      console.warn(
        '[App Insights] REACT_APP_APPINSIGHTS_CONNECTION_STRING is not set. Tracking disabled.'
      );
    } else {
      appInsights.loadAppInsights();
      console.log(
        '[App Insights] Initialized with connection string',
        `(environment: ${environment})`
      );
    }
  } else {
    console.log(
      '[App Insights] Disabled (not production and REACT_APP_ENABLE_APPINSIGHTS !== true)'
    );
  }

  // Add session/correlation IDs and environment to all telemetry
  // CRITICAL: Read IDs dynamically from storage, not from const values
  // This ensures we pick up signup IDs stored in localStorage after module initialization
  appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
    envelope.tags = envelope.tags || {};
    envelope.data = envelope.data || {};

    // Dynamically read current session/correlation IDs from storage
    // Check signup IDs first (localStorage), then fall back to app IDs (sessionStorage)
    const currentSessionId =
      localStorage.getItem('signup_session_id') ||
      sessionStorage.getItem('app_session_id') ||
      sessionId;
    const currentCorrelationId =
      localStorage.getItem('signup_correlation_id') ||
      sessionStorage.getItem('app_correlation_id') ||
      correlationId;

    envelope.data.sessionId = currentSessionId;
    envelope.data.correlationId = currentCorrelationId;
    envelope.data.environment = environment;

    // Add user_id and email_domain to all telemetry (including pageViews)
    // This must be done in the initializer since these need TokenService to be ready
    // Only set if not already present to preserve explicitly-passed values
    if (!envelope.data.user_id) {
      const userId = getUserId();
      if (userId) {
        envelope.data.user_id = userId;
      }
    }

    if (!envelope.data.email_domain) {
      const emailDomain = getUserEmailDomain();
      if (emailDomain) {
        envelope.data.email_domain = emailDomain;
      }
    }
  });
} catch (error) {
  // Silently fail - tracking initialization should never break app startup
  console.warn('[App Insights] Initialization failed:', error);
}

export const trackTrace = (message: string, props?: object) => {
  try {
    console.log(
      `[App Insights] Tracing message with session id ${sessionId}: ${message}`
    );
    appInsights.trackTrace({
      message,
      properties: { ...props },
    });
  } catch (error) {
    // Silently fail - tracking should never break core functionality
    console.warn('[App Insights] trackTrace failed:', error);
  }
};

export const trackEvent = (name: string, props?: object) => {
  try {
    console.log(
      `[App Insights] Tracing event with session id ${sessionId}: ${name}`
    );
    appInsights.trackEvent({ name }, { ...props });
  } catch (error) {
    // Silently fail - tracking should never break core functionality
    console.warn('[App Insights] trackEvent failed:', error);
  }
};

export const trackException = (error: Error, props?: object) => {
  try {
    console.log(
      `[App Insights] Tracing exception with session id ${sessionId}: ${error}`
    );
    appInsights.trackException({
      exception: error,
      properties: { ...props },
    });
  } catch (trackingError) {
    // Silently fail - tracking should never break core functionality
    console.warn('[App Insights] trackException failed:', trackingError);
  }
};

// Deduplication for page views - prevents duplicate tracking from React re-renders
let lastPageView: { name?: string; url?: string; timestamp: number } | null =
  null;
const PAGE_VIEW_DEDUPE_MS = 100; // Ignore duplicate page views within 100ms

export const trackPageView = (name?: string, url?: string, props?: object) => {
  try {
    const now = Date.now();

    // Deduplicate: skip if same page view was tracked within the dedupe window
    if (
      lastPageView &&
      lastPageView.name === name &&
      lastPageView.url === url &&
      now - lastPageView.timestamp < PAGE_VIEW_DEDUPE_MS
    ) {
      console.log(
        `[App Insights] Skipping duplicate page view: ${name} (within ${PAGE_VIEW_DEDUPE_MS}ms)`
      );
      return;
    }

    lastPageView = { name, url, timestamp: now };

    console.log(
      `[App Insights] Tracing page view with session id ${sessionId}: ${name}`
    );
    appInsights.trackPageView({
      name,
      uri: url,
      properties: { ...props },
    });
  } catch (error) {
    // Silently fail - tracking should never break core functionality
    console.warn('[App Insights] trackPageView failed:', error);
  }
};

// Interface for TokenService access via window to avoid circular dependency
interface WindowWithTokenService extends Window {
  __tokenService?: {
    getUserInfo: () => { id: string; email?: string } | null;
  };
}

/**
 * Get the current authenticated user's ID (Auth0 sub) for tracking
 * Returns null if no user is authenticated
 * This is safe to use in analytics - it's not PII
 *
 * Note: We use a getter function instead of direct import to avoid
 * circular dependencies between monitoring and TokenService
 */
export const getUserId = (): string | null => {
  try {
    // Access TokenService from window to avoid circular dependency
    // TokenService is a singleton that's initialized during app startup
    const win = window as WindowWithTokenService;
    if (typeof window !== 'undefined' && win.__tokenService) {
      return win.__tokenService.getUserInfo()?.id || null;
    }
    return null;
  } catch (error) {
    // If TokenService not available or errors, return null
    return null;
  }
};

/**
 * Get the email domain of the current authenticated user for tracking
 * Returns null if no user is authenticated or email is not available
 * Only returns the domain part (e.g., "example.com") - not PII
 */
export const getUserEmailDomain = (): string | null => {
  try {
    const win = window as WindowWithTokenService;
    if (typeof window !== 'undefined' && win.__tokenService) {
      const email = win.__tokenService.getUserInfo()?.email;
      if (email && email.includes('@')) {
        return email.split('@')[1];
      }
    }
    return null;
  } catch (error) {
    return null;
  }
};

export { appInsights, reactPlugin, sessionId, correlationId, environment };
